import React, { useEffect, useState } from 'react';
import { format, parseISO } from 'date-fns';
import { nl } from 'date-fns/locale';
import { toast } from 'sonner';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { leiderschapApi } from '../api';
import { MINUTES_STATUSES, findClassName, findLabel } from '../labels';
import type { LeadershipMinutes } from '../types';

export default function MinutesTab({ meetingId, canManage }: { meetingId: string; canManage: boolean }) {
  const [minutes, setMinutes] = useState<LeadershipMinutes | null>(null);
  const [content, setContent] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const fetchMinutes = async () => {
    try {
      const data = await leiderschapApi.getMinutes(meetingId);
      setMinutes(data);
      setContent(data.content);
    } catch (error) {
      setMinutes(null);
      setContent('');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMinutes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [meetingId]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const updated = await leiderschapApi.saveMinutes(meetingId, content);
      setMinutes(updated);
      toast.success('Notulen opgeslagen');
    } catch (error) {
      toast.error('Opslaan mislukt');
    } finally {
      setSaving(false);
    }
  };

  const handleFinalize = async () => {
    if (!window.confirm('Notulen vaststellen? Een latere wijziging heropent ze als concept.')) return;
    setSaving(true);
    try {
      const updated = await leiderschapApi.finalizeMinutes(meetingId);
      setMinutes(updated);
      toast.success('Notulen vastgesteld');
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Vaststellen mislukt');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return null;

  const isFinalized = minutes?.status === 'vastgesteld';

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <Badge className={findClassName(MINUTES_STATUSES, minutes?.status ?? 'concept')}>
            {findLabel(MINUTES_STATUSES, minutes?.status ?? 'concept')}
          </Badge>
          {isFinalized && minutes?.approved_at && (
            <span className="text-xs text-slate-500">
              Vastgesteld op {format(parseISO(minutes.approved_at), "d MMMM yyyy 'om' HH:mm", { locale: nl })}
            </span>
          )}
        </div>
      </div>

      <Textarea
        value={content}
        onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setContent(e.target.value)}
        rows={12}
        placeholder="Notulen..."
        disabled={!canManage}
      />

      {canManage && (
        <div className="flex gap-2">
          <Button variant="outline" onClick={handleSave} disabled={saving}>Opslaan</Button>
          <Button onClick={handleFinalize} disabled={saving || !content.trim() || isFinalized}>Vaststellen</Button>
        </div>
      )}

      {minutes && minutes.revisions.length > 0 && (
        <div className="border-t border-slate-100 pt-4">
          <p className="mb-2 text-sm font-medium text-slate-700">Revisiehistorie</p>
          <div className="space-y-2">
            {minutes.revisions.map((revision, index) => (
              <details key={index} className="rounded-lg border border-slate-100 p-3">
                <summary className="cursor-pointer text-xs text-slate-500">
                  Versie van {format(parseISO(revision.revised_at), "d MMMM yyyy 'om' HH:mm", { locale: nl })}
                </summary>
                <p className="mt-2 whitespace-pre-wrap text-sm text-slate-600">{revision.content}</p>
              </details>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
